const STORAGE_PREFIX = "konstantino:scroll:";
const META_PREFIX = "konstantino:scroll-meta:";
const LEGACY_KEYS = [
  "scrollPosition",
  "scrollList",
  "saveScrollPosition",
  "scrollProduct",
];

let legacyKeysCleared = false;
let pendingNavigationAction = null;
let lastNavigationAction = null;
let routeScrollMetaProvider = null;

export function getScrollKey(pathname, search = "") {
  return `${pathname}${search || ""}`;
}

function getStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  return window.sessionStorage;
}

export function clearLegacyScrollKeys() {
  if (legacyKeysCleared || typeof window === "undefined") {
    return;
  }

  LEGACY_KEYS.forEach((key) => {
    window.localStorage.removeItem(key);
  });

  legacyKeysCleared = true;
}

export function registerRouteScrollMetaProvider(provider) {
  routeScrollMetaProvider = provider;
}

export function setPendingNavigationAction(action) {
  pendingNavigationAction = action;
  lastNavigationAction = action;
}

export function consumePendingNavigationAction() {
  const action = pendingNavigationAction;
  pendingNavigationAction = null;
  return action;
}

export function getLastNavigationAction() {
  return lastNavigationAction;
}

export function saveScrollPosition(key, y, meta) {
  const storage = getStorage();
  if (!storage || !key) {
    return;
  }

  const pathname = key.split("?")[0];
  const search = key.includes("?") ? key.slice(key.indexOf("?")) : "";

  if (!shouldSaveScrollForRoute(pathname, search)) {
    return;
  }

  const position = Math.max(0, Math.round(y));
  storage.setItem(`${STORAGE_PREFIX}${key}`, String(position));

  const resolvedMeta =
    meta || (routeScrollMetaProvider ? routeScrollMetaProvider(key) : null);

  if (resolvedMeta && Object.keys(resolvedMeta).length) {
    storage.setItem(`${META_PREFIX}${key}`, JSON.stringify(resolvedMeta));
  }
}

export function getScrollPosition(key) {
  const storage = getStorage();
  if (!storage || !key) {
    return null;
  }

  const value = storage.getItem(`${STORAGE_PREFIX}${key}`);
  if (value == null) {
    return null;
  }

  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getScrollMeta(key) {
  const storage = getStorage();
  if (!storage || !key) {
    return null;
  }

  const value = storage.getItem(`${META_PREFIX}${key}`);
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
}

export function restoreScrollPosition(targetY, options = {}) {
  const { maxAttempts = 80, interval = 100, maxDuration = 8000 } = options;

  if (typeof window === "undefined") {
    return () => {};
  }

  let attempt = 0;
  let stopped = false;
  let timerId = null;
  let resizeObserver = null;

  const applyScroll = () => {
    window.scrollTo(0, targetY);
  };

  const cleanup = () => {
    stopped = true;

    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }

    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  };

  const scheduleRetry = () => {
    if (stopped || attempt >= maxAttempts) {
      cleanup();
      return;
    }

    timerId = setTimeout(tryRestore, interval);
  };

  const tryRestore = () => {
    if (stopped) {
      return;
    }

    applyScroll();
    attempt += 1;

    const diff = Math.abs(window.scrollY - targetY);
    const docHeight = document.documentElement.scrollHeight;
    const viewportCanReach =
      docHeight >= targetY + window.innerHeight * 0.25 || targetY <= 0;

    if (diff <= 2 && viewportCanReach) {
      cleanup();
      return;
    }

    if (attempt >= maxAttempts) {
      cleanup();
      return;
    }

    scheduleRetry();
  };

  applyScroll();

  if (typeof ResizeObserver !== "undefined") {
    resizeObserver = new ResizeObserver(() => {
      if (!stopped) {
        applyScroll();
      }
    });
    resizeObserver.observe(document.documentElement);
    resizeObserver.observe(document.body);
  }

  tryRestore();
  setTimeout(cleanup, maxDuration);

  return cleanup;
}

export function scrollToTop(options = {}) {
  const { behavior = "auto" } = options;
  window.scrollTo({ top: 0, left: 0, behavior });
}

export function applyBackNavigationScroll(pathname, search) {
  const routeKey = getScrollKey(pathname, search);
  const canRestore = isScrollRestorableRoute(pathname, search);
  const savedPosition = canRestore ? getScrollPosition(routeKey) : null;

  if (canRestore && savedPosition != null && savedPosition > 0) {
    return restoreScrollPosition(savedPosition);
  }

  scrollToTop();
  return null;
}

export function applyForwardNavigationScroll(pathnameChanged) {
  if (pathnameChanged) {
    scrollToTop();
  }
}

export function isCollectionRoute(pathname = "") {
  const path = pathname.split("?")[0];
  return /^\/(women|men)\/[^/]+\/[^/]+$/.test(path);
}

export function isSearchRoute(pathname = "") {
  const path = pathname.split("?")[0];
  return path === "/search/results";
}

export function isScrollRestorableRoute(pathname = "", search = "") {
  const routeKey = getScrollKey(pathname, search);
  const path = routeKey.split("?")[0];
  return isCollectionRoute(path) || isSearchRoute(path);
}

export function shouldSaveScrollForRoute(pathname = "", search = "") {
  return isScrollRestorableRoute(pathname, search);
}
